import React from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Target,
  Heart,
  Shield,
  ArrowRight,
  Zap,
  CheckCircle2,
  Users,
  Settings,
  Mic,
  Network,
  RefreshCw,
  BarChart3,
  Lock,
  Layers,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const values = [
  { icon: Target, title: 'Customer outcomes first', desc: 'Measurable impact matters. We track resolved tickets and real efficiency gains, not feature counts.' },
  { icon: Shield, title: 'Trust and safety', desc: 'Security and governance are built in: verification, role controls, audit logs, and approval gates.' },
  { icon: Layers, title: 'Simple, scalable design', desc: 'Build once, reuse across systems and channels. Complexity is our problem, not yours.' },
  { icon: Network, title: 'Integration over replacement', desc: 'We meet customers where they are, working with the tools enterprises already rely on.' },
  { icon: BarChart3, title: 'Continuous improvement', desc: 'Analytics, QA, and iteration are part of delivery, not afterthoughts.' },
];

const whatWeDo = [
  { icon: Mic, title: 'AI Voice Agents', desc: 'Natural, secure conversations across voice, chat, and email' },
  { icon: Settings, title: 'Workflow Automation', desc: 'Execute backend actions, not just answer questions' },
  { icon: Network, title: 'Enterprise Integrations', desc: 'Connect CRM, ITSM, identity, and custom apps in real time' },
];

const deliveryAreas = [
  {
    title: 'IT Helpdesk Automation',
    items: ['Password resets & account unlocks', 'Onboarding / offboarding', 'Software installs', 'Hardware workflows'],
  },
  {
    title: 'Customer Care Automation',
    items: ['Order status & returns', 'Billing support', 'Case routing', 'Appointment scheduling'],
  },
];

const phases = [
  { num: '1', title: 'Discovery & Pre-Assessment', desc: 'We review your current workflows, systems, and the highest-volume requests.' },
  { num: '2', title: 'POC (Proof of Concept)', desc: 'We build and demo a small set of high-impact use cases using your environment and data.' },
  { num: '3', title: 'Pilot', desc: 'We expand to more intents/workflows, add governance controls, and measure outcomes.' },
  { num: '4', title: 'Production Rollout', desc: 'We scale across channels, teams, and integrations with ongoing optimization.' },
];

const differentiators = [
  { icon: Zap, title: 'End-to-end execution', desc: 'The agent not only answers. It triggers workflows and updates systems.' },
  { icon: Network, title: 'Integration-first', desc: 'Works with CRMs, ITSM tools, identity systems, contact centers, and custom APIs' },
  { icon: Lock, title: 'Security-aware design', desc: 'Verification, role controls, audit logs, and approval gates' },
  { icon: Layers, title: 'Omnichannel by design', desc: 'Voice, chat, and email experiences share a unified intelligence layer' },
  { icon: RefreshCw, title: 'Fast time-to-value', desc: 'Start with the top use cases, prove ROI, then scale' },
];

const engagementOptions = [
  'POCs and demos (fast validation)',
  'Pilot programs (measured rollout)',
  'Production deployments (scalable automation)',
  'Ongoing optimization (new intents, workflow expansion, analytics improvements)',
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead page="about" />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl space-y-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600">About digian.ai</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
              We build AI voice agents and enterprise workflow automation
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              We help organizations deliver faster support across IT Helpdesk and Customer Care by combining AI agents with real workflow execution and deep enterprise integrations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/demo"><Button size="lg">Request a Demo</Button></Link>
              <Link to="/contact">
                <Button size="lg" variant="outline">Talk to an Architect <ArrowRight className="ml-2 w-4 h-4" /></Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp} className="space-y-5">
              <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600">Our Mission</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">
                Make enterprise support simple, fast, and always available.
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Our mission is to help teams reduce repetitive work and improve service quality using AI, without replacing existing systems. We focus on practical automation that integrates with the tools enterprises already rely on.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.15 }}
              className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl p-8 text-white space-y-4">
              <p className="text-sm font-semibold uppercase tracking-widest text-indigo-200">The digian.ai difference</p>
              <p className="text-xl font-bold leading-relaxed">
                AI Agents that don't stop at conversation. They complete the work.
              </p>
              <p className="text-indigo-100 text-sm leading-relaxed">
                Most AI tools answer questions. digian.ai triggers the backend action, updates the system, and closes the loop automatically.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">What We Do</p>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">digian.ai combines:</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {whatWeDo.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border border-indigo-100 p-6 hover:shadow-md hover:-translate-y-1 transition-all">
                  <div className="w-11 h-11 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">{w.title}</h3>
                  <p className="text-sm text-slate-500">{w.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeUp}>
            <h3 className="text-xl font-bold text-slate-900 mb-6">Where we deliver value today:</h3>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            {deliveryAreas.map((area, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl border-l-4 border-indigo-500 p-6">
                <h4 className="font-bold text-slate-900 mb-4">{area.title}</h4>
                <ul className="space-y-2">
                  {area.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">How We Work With Customers</p>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">A practical path from POC to production.</h2>
            <p className="text-slate-500">We work with customers in clear phases:</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {phases.map((phase, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="relative bg-slate-50 rounded-xl border border-slate-200 p-6 hover:border-indigo-300 hover:shadow-md transition-all">
                <div className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm mb-4">
                  {phase.num}
                </div>
                <h4 className="font-bold text-slate-900 mb-2 text-sm">{phase.title}</h4>
                <p className="text-sm text-slate-500 leading-relaxed">{phase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">What Makes digian.ai Different</p>
            <h2 className="text-3xl font-bold text-slate-900">Built for real enterprise environments.</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentiators.map((d, i) => {
              const Icon = d.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-white rounded-xl border border-indigo-100 p-5 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{d.title}</h4>
                    <p className="text-sm text-slate-500">{d.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-3">Our Values</p>
            <h2 className="text-3xl font-bold text-slate-900">What guides every decision we make</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div key={v.title} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                  <div className="w-11 h-11 rounded-xl bg-indigo-100 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Engagement Model */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp} className="space-y-5">
              <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600">Engagement Model</p>
              <h2 className="text-3xl font-bold text-slate-900">
                Whether you're exploring AI for the first time or modernizing an existing support stack
              </h2>
              <p className="text-slate-600">We can support:</p>
              <ul className="space-y-3">
                {engagementOptions.map((opt, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                    {opt}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.15 }}
              className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl p-8 text-white space-y-4">
              <Users className="w-10 h-10 text-indigo-200" />
              <h3 className="text-2xl font-bold">Let's Build Your AI Support Experience</h3>
              <p className="text-indigo-100 leading-relaxed">
                If you want to modernize IT support or customer care with AI voice and workflow automation, we'd love to talk.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link to="/demo">
                  <Button variant="secondary" size="lg">Book a Demo</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-indigo-600">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
